import React from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';

import { Avatar, Box, Chip, Grid, Stack, Typography, useTheme } from '@mui/material';

import { Patient } from '../../patient';

import styles from './styles';
import SummaryDetailsBlock from './components/SummaryDetailsBlock';
import moment from 'moment';
import { Organisation } from '../../organisation';
import { Lab } from '../../lab';
import { Category, SpecialityItem } from '../../speciality';
import { Pot, enumSampleTypeDescriptions } from '../../pots';
import { MultipleSpecialityPathway } from '../../speciality_pathway';
import Pathway from '../../../../../../components/PathwayBuilder/Common';
import { SenderUser } from '../../../../../../react-app-env';
import { UploadFile } from '@mui/icons-material';
import { UploadItem } from '../../../../../../utils/types';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  patient: Patient;
  accessionNumber?: string;
  organisation?: Organisation;
  lab?: Lab;
  pots: Pot[];
  specialityPathwayValues: MultipleSpecialityPathway;
  specialityPathway: Record<string, Pathway>;
  potPathway: Record<string, Pathway>;
  specialityCategories: Category[];
  specialities: SpecialityItem[];
  user: SenderUser;
  files: UploadItem[];
  setStep: (step: number) => void;
}

const StepReview = ({
  classes,
  patient,
  accessionNumber,
  organisation,
  lab,
  user,
  pots,
  potPathway,
  specialityCategories,
  specialities,
  specialityPathway,
  specialityPathwayValues,
  files,
  setStep,
}: Props): React.ReactElement => {
  const theme = useTheme();

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SummaryDetailsBlock
            title="Patient"
            onEdit={() => setStep(0)}
            backgroundColor={theme.palette.primary.neutral}
            dataBlocks={[
              {
                data: [
                  {
                    label: 'Name',
                    value: (
                      <b>
                        {patient.first_name} {patient.last_name}
                      </b>
                    ),
                  },
                  { label: 'NHS number', value: patient.nhs_number },
                  { label: 'Medical record number', value: patient.mrn },
                  { label: 'Date of Birth', value: moment(patient.dob).format('DD/MM/YYYY') },
                ],
              },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <SummaryDetailsBlock
            title="Documents"
            onEdit={() => setStep(1)}
            dataBlocks={[
              {
                data: [
                  {
                    label: 'File(s)',
                    value: files.length ? (
                      <Box display={'flex'} flexDirection={'column'} gap={1}>
                        {files.map((file) => (
                          <Box display={'flex'} alignItems={'center'} gap={1}>
                            <Avatar sx={(theme) => ({ bgcolor: `${theme.palette.primary.main}40`, width: 32, height: 32 })}>
                              <UploadFile fontSize="small" sx={(theme) => ({ fill: theme.palette.primary.main })} />
                            </Avatar>
                            <Typography variant="body2">{file instanceof File ? file.name : file.document_name}</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      'No files uploaded'
                    ),
                  },
                ],
              },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <SummaryDetailsBlock
            title="Sender"
            onEdit={() => setStep(4)}
            dataBlocks={[
              {
                data: [
                  {
                    label: 'Organisation',
                    value: organisation?.name ?? '',
                  },
                  {
                    label: 'Lab',
                    value: lab?.name ?? '',
                  },
                  { label: 'Requestor', value: user?.full_name ?? '' },
                  { label: 'Accession number', value: accessionNumber ?? '' },
                ],
              },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <SummaryDetailsBlock
            title="Samples"
            onEdit={() => setStep(3)}
            subTitle={`${specialityCategories.length} specialit${specialityCategories.length > 1 ? 'ies' : 'y'}, ${pots.length} sample${pots.length > 1 ? 's' : ''}`}
            dataBlocks={specialityCategories.map((category) => {
              const potsInCategory = pots.filter((pot) => pot.speciality_category_id === category.id);

              const baseData = [
                {
                  label: 'Speciality',
                  value: <b>{specialities.find((speciality) => speciality.categories.some((c) => c.id === category.id))?.name ?? ''}</b>,
                },
                {
                  label: 'Category',
                  value: category.name,
                },
              ];

              const pathwaySteps = specialityPathway[category.id]?.steps ?? [];
              const selectedValues = specialityPathwayValues[category.id];

              const stepData = pathwaySteps.map((step) => {
                const selectedId = selectedValues.pathway[step.id];
                const selectedOption = step.options.find((opt) => opt.id === selectedId);
                return {
                  label: step.name,
                  value: selectedOption?.label ?? '---',
                };
              });

              const samples = {
                label: `Sample${potsInCategory.length > 1 ? 's' : ''}`,
                value: (
                  <Stack spacing={2}>
                    {potsInCategory.map((pot) => (
                      <Box key={pot.id}>
                        <Box display={'flex'} gap={1}>
                          <Typography>{pot.name.replace('Sample ', '')}</Typography>
                          <Chip label={enumSampleTypeDescriptions[pot.sample_type]} size="small" />
                        </Box>
                        <Typography fontSize={14} color="grey.600">
                          {potPathway[pot.speciality_category_id]?.steps
                            .map((step) => {
                              const value = pot.pathway[step.id];
                              return value ? step.options.find((opt) => opt.id === value)?.label : null;
                            })
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ),
              };

              return {
                data: [...baseData, ...stepData, samples],
              };
            })}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default withStyles(styles)(StepReview);
